import React, { useState, useEffect } from "react";
import axios from "axios";
import { UserCheck, Plus, Pencil, Trash2, X, Loader2, CheckCircle2, AlertCircle, MapPin, Wallet } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const rp = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const EMPTY = { nama: "", alamat: "", no_hp: "", wilayah_ids: [], honor_per_pelanggan: 0, status: "aktif" };

const PenarikForm = ({ initial, wilayah, onClose, onSuccess }) => {
  const [form, setForm] = useState(initial || EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleWilayah = (id) => setForm((f) => ({
    ...f,
    wilayah_ids: f.wilayah_ids.includes(id) ? f.wilayah_ids.filter((x) => x !== id) : [...f.wilayah_ids, id],
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const payload = { ...form, honor_per_pelanggan: Number(form.honor_per_pelanggan) || 0 };
      if (initial?.id) await axios.put(`${BACKEND_URL}/api/penarik/${initial.id}`, payload, { withCredentials: true });
      else await axios.post(`${BACKEND_URL}/api/penarik`, payload, { withCredentials: true });
      onSuccess(); onClose();
    } catch (err) { setError(err.response?.data?.detail || "Gagal simpan"); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-[#0e1628]">
          <h2 className="text-base font-bold text-white flex items-center gap-2"><UserCheck className="w-4 h-4 text-orange-400" />{initial?.id ? "Edit Penarik" : "Tambah Penarik/Kolektor"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          {error && <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-center gap-2" data-testid="penarik-form-error"><AlertCircle className="w-4 h-4" />{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Nama Penarik *</label>
              <input required type="text" value={form.nama} onChange={(e) => setField("nama", e.target.value)} placeholder="Nama kolektor" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="penarik-nama-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">No. HP / WA</label>
              <input type="text" value={form.no_hp} onChange={(e) => setField("no_hp", e.target.value)} placeholder="0812xxxx" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="penarik-hp-input" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Alamat</label>
            <input type="text" value={form.alamat} onChange={(e) => setField("alamat", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="penarik-alamat-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Honor per Pelanggan (Rp)</label>
              <input type="number" min={0} value={form.honor_per_pelanggan} onChange={(e) => setField("honor_per_pelanggan", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="penarik-honor-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Status</label>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="penarik-status-select">
                <option value="aktif">Aktif</option>
                <option value="nonaktif">Nonaktif</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Wilayah yang Ditangani (pilih ≥1)</label>
            <p className="text-[10px] text-slate-500 mb-2">Catatan: satu wilayah hanya boleh dipegang satu penarik.</p>
            <div className="grid grid-cols-2 gap-1.5 max-h-52 overflow-y-auto p-1" data-testid="penarik-wilayah-list">
              {wilayah.length === 0 && <p className="text-[11px] text-slate-500 italic col-span-2">Belum ada wilayah. Tambahkan di Master Data → Wilayah.</p>}
              {wilayah.map((w) => {
                const active = form.wilayah_ids.includes(w.id);
                return (
                  <button type="button" key={w.id} onClick={() => toggleWilayah(w.id)} data-testid={`penarik-wilayah-${w.id}`}
                    className={`px-2.5 py-2 rounded-lg text-[11px] font-semibold text-left border transition-colors ${active ? "bg-orange-500/20 text-orange-200 border-orange-500/50" : "bg-slate-900 text-slate-400 border-slate-700 hover:border-slate-600"}`}>
                    {active ? "✓ " : ""}{w.nama_wilayah} <span className="text-slate-500 font-mono">{w.kode_wilayah}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2 pt-2 border-t border-slate-800">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700">Batal</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-orange-600 to-amber-600 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="penarik-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}{initial?.id ? "Update" : "Simpan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const MasterPenarikPage = () => {
  const [items, setItems] = useState([]);
  const [wilayah, setWilayah] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [notif, setNotif] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [p, w] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/penarik`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/master/wilayah`, { withCredentials: true }),
      ]);
      setItems(p.data || []); setWilayah(w.data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const wilNama = (id) => wilayah.find((w) => w.id === id)?.nama_wilayah || id;

  const handleDelete = async (id, nama) => {
    if (!window.confirm(`Hapus penarik "${nama}"?`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/penarik/${id}`, { withCredentials: true }); load(); }
    catch (err) { alert(err.response?.data?.detail || "Gagal hapus"); }
  };

  const postHonor = async () => {
    if (!window.confirm("Posting honor semua penarik bulan berjalan ke Pengeluaran Operasional?")) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/penarik/honor/post`, {}, { withCredentials: true });
      setNotif(res.data.message); setTimeout(() => setNotif(""), 5000);
    } catch (err) { alert(err.response?.data?.detail || "Gagal posting honor"); }
  };

  return (
    <div className="space-y-4" data-testid="penarik-page">
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl flex flex-col md:flex-row justify-between md:items-center gap-3">
        <div>
          <span className="px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 text-[10px] font-bold uppercase">Master Data</span>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">Data Penarik / Kolektor</h1>
          <p className="text-xs text-slate-400 mt-0.5">Petugas tagih ke rumah pelanggan per wilayah beserta honor per pelanggan.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={postHonor} data-testid="post-honor-btn" className="px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-xs font-bold rounded-xl flex items-center gap-1.5"><Wallet className="w-4 h-4 text-emerald-400" /> Posting Honor</button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-4 py-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white text-xs font-bold rounded-xl shadow-lg shadow-orange-600/30 flex items-center gap-2" data-testid="add-penarik-btn"><Plus className="w-4 h-4" />Tambah Penarik</button>
        </div>
      </div>

      {notif && <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-xl text-xs flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {notif}</div>}

      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl overflow-x-auto">
        <table className="w-full text-left text-xs" data-testid="penarik-table">
          <thead>
            <tr className="border-b border-slate-800 text-slate-400">
              <th className="py-2.5 px-3">Nama</th>
              <th className="py-2.5 px-3">Kontak</th>
              <th className="py-2.5 px-3">Wilayah</th>
              <th className="py-2.5 px-3">Honor/Pelanggan</th>
              <th className="py-2.5 px-3 text-center">Status</th>
              <th className="py-2.5 px-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {loading ? (
              <tr><td colSpan={6} className="py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Memuat...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center text-slate-500 italic" data-testid="penarik-empty">Belum ada penarik.</td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="hover:bg-slate-900/40" data-testid={`penarik-row-${p.id}`}>
                <td className="py-2.5 px-3"><p className="font-bold text-white">{p.nama}</p><span className="text-[10px] text-slate-400">{p.alamat}</span></td>
                <td className="py-2.5 px-3 text-slate-300 font-mono">{p.no_hp || "-"}</td>
                <td className="py-2.5 px-3">
                  <div className="flex flex-wrap gap-1">
                    {(p.wilayah_ids || []).length === 0 ? <span className="text-slate-600">—</span> : (p.wilayah_ids || []).map((id) => (
                      <span key={id} className="px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-300 text-[10px] font-semibold flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{wilNama(id)}</span>
                    ))}
                  </div>
                </td>
                <td className="py-2.5 px-3 font-mono text-emerald-400 font-bold">{rp(p.honor_per_pelanggan)}</td>
                <td className="py-2.5 px-3 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${p.status === "aktif" ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>{p.status}</span></td>
                <td className="py-2.5 px-3">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-1 rounded bg-slate-800 hover:bg-amber-900/60 text-amber-400" data-testid={`edit-penarik-${p.id}`}><Pencil className="w-3 h-3" /></button>
                    <button onClick={() => handleDelete(p.id, p.nama)} className="p-1 rounded bg-slate-800 hover:bg-rose-900/60 text-rose-400" data-testid={`delete-penarik-${p.id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && <PenarikForm initial={editing} wilayah={wilayah} onClose={() => setShowForm(false)} onSuccess={load} />}
    </div>
  );
};
